import { useEffect, useState } from 'react';
import { Card, Heading, Button, Pane, Checkbox, majorScale, toaster } from 'evergreen-ui';
import { RemoveIcon, EditIcon, DocumentIcon } from 'evergreen-ui';
import { removeCommunication } from '../services/communicationServices';
import { markCommunicationAsRead } from '../services/communicationServices';
import { getUserDetailsByIds } from '../services/userServices';
import '../styles/responsive.css';

const CommunicationList = ({ communications, communicationViews, setCommunications, handleSelectCommunication, userId, userRole }) => {
    const [readCommunications, setReadCommunications] = useState(new Set(communicationViews.map(view => view.communicationId)));
    const [userDetails, setUserDetails] = useState({});
    const [hideRead, setHideRead] = useState(false); 
    const [sortField, setSortField] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc'); 
    const [selectedCommunication, setSelectedCommunication] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            const userIds = communicationViews
                .filter(view => view && view.user_id !== undefined)
                .map(view => view.user_id);

            if (userIds.length === 0) {
                console.warn('Nessun ID utente valido trovato in communicationViews:', communicationViews);
                return;
            }

            const uniqueUserIds = [...new Set(userIds)];

            try {
                const userDetailsResponse = await getUserDetailsByIds(uniqueUserIds);
                const userDetailsMap = userDetailsResponse.reduce((acc, user) => {
                    acc[user.id] = user;
                    return acc;
                }, {});

                setUserDetails(userDetailsMap);
            } catch (error) {
                console.error('Errore nel recupero dei dettagli degli utenti:', error);
            }
        };

        if (Array.isArray(communicationViews) && communicationViews.length > 0) {
            fetchUserDetails();
        }
    }, [communicationViews]);

    const handleRemoveCommunication = async (index) => {
        try {
            const communicationToRemove = communications[index];
            await removeCommunication(communicationToRemove.id); 
            setCommunications((prev) => prev.filter((_, i) => i !== index));
            toaster.notify('Comunicazione rimossa con successo.');
        } catch (error) {
            console.error('Errore nella rimozione della comunicazione:', error);
            toaster.danger('Errore nella rimozione della comunicazione.');
        }
    };

    const handleMarkAsRead = async (id) => {
        if (readCommunications.has(id)) return; 
        try {
            await markCommunicationAsRead(id, userId);
            setReadCommunications((prev) => new Set(prev).add(id));
            toaster.success('Comunicazione segnata come letta.');
        } catch (error) {
            console.error('Errore nel segnare come letta:', error);
            toaster.danger('Errore nel segnare come letta.');
        }
    };   
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    useEffect(() => {
        const updatedReadCommunications = new Set(
            communicationViews
                .filter((view) => view.user_id === userId)
                .map((view) => view.communication_id)
        );
        setReadCommunications(updatedReadCommunications);
    }, [communicationViews, userId]);

    const filteredCommunications = hideRead
        ? communications.filter((communication) => !readCommunications.has(communication.id))
        : communications;

    const sortedCommunications = [...filteredCommunications].sort((a, b) => {
        const dateA = new Date(sortOrder === 'asc' ? a[sortField] : b[sortField]);
        const dateB = new Date(sortOrder === 'asc' ? b[sortField] : a[sortField]);
        return dateA - dateB;
    });

    const handleSelectCommunicationToggle = (communication) => {
        setSelectedCommunication((prev) => (prev?.id === communication.id ? null : communication));
    };

    return (
        <Pane>
            <Heading size={600} marginBottom={16}>
                Comunicazioni inviate
            </Heading>
            {(userRole === 'operatore' || userRole === 'educatore') && (
                <Pane marginBottom={majorScale(2)}>
                    <Checkbox
                        label="Nascondi già lette"
                        checked={hideRead}
                        onChange={(e) => setHideRead(e.target.checked)}
                    />
                </Pane>
            )}
            <Pane marginBottom={majorScale(2)} className="responsive-button-group" display="flex" gap={16} alignItems="center">
                <Button
                    appearance="primary"
                    intent="success"
                    onClick={() => {
                        setSortField('timestamp');
                        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                    }}
                >
                    Ordina per data di creazione {sortField === 'timestamp' && (sortOrder === 'asc' ? '↑' : '↓')}
                </Button>
                <Button
                    appearance="primary"
                    intent="success"
                    onClick={() => {
                        setSortField('last_modified');
                        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                    }}
                >
                    Ordina per data di modifica {sortField === 'last_modified' && (sortOrder === 'asc' ? '↑' : '↓')}
                </Button>
            </Pane>
            {Array.isArray(sortedCommunications) && sortedCommunications.map((communication, index) => (
                <Card key={index} className="card-flex" elevation={1} padding={16} marginBottom={8}>
                    <Pane className="card-header">
                        <Heading size={500} onClick={() => handleSelectCommunicationToggle(communication)} style={{ cursor: 'pointer' }}>
                            {communication.title}
                        </Heading>
                        <Pane>
                            <div className="date-line">Creato: {formatDate(communication.timestamp)}</div>
                            {communication.last_modified && (
                                <div className="date-line">Modificato: {formatDate(communication.last_modified)}</div>
                            )}
                        </Pane>
                    </Pane>
                    {selectedCommunication?.id === communication.id && (
                        <>
                            <Pane className="card-body" marginTop={16}>
                                <p>{communication.body}</p>
                                {Array.isArray(communication.attachments) && communication.attachments.length > 0 && (
                                    <Pane marginTop={16}>
                                        <Heading size={400} marginBottom={8}>Allegati:</Heading>
                                        <ul style={{ paddingLeft: 16 }}>
                                            {communication.attachments.map((attachment, idx) => (
                                                <li key={idx}>
                                                    <a href={attachment.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        <DocumentIcon flexShrink={0} /> {attachment.name}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </Pane>
                                )}
                                {userRole === 'direttore' && (
                                    <Pane marginTop={majorScale(2)}>
                                        <Heading size={400}>Visualizzato da:</Heading>
                                        <ul>
                                            {communicationViews && communicationViews.length > 0 ? (
                                                communicationViews
                                                    .filter((view) => view.communication_id === communication.id)
                                                    .map((view) => (
                                                        <li key={`${view.communication_id}-${view.user_id}`}>
                                                            {userDetails[view.user_id]?.nome || 'Nome non disponibile'} {userDetails[view.user_id]?.cognome || ''}
                                                            {view.viewed_at ? ` - ${formatDate(view.viewed_at)}` : ''}
                                                        </li>
                                                    ))
                                            ) : (
                                                <li>Nessuno ha ancora visualizzato questa comunicazione.</li>
                                            )}
                                        </ul>
                                    </Pane>
                                )}
                                {(userRole === 'operatore' || userRole === 'educatore') && (
                                    <Pane marginTop={majorScale(2)}>
                                        <Checkbox
                                            label={readCommunications.has(communication.id) ? "Letto" : "Segna come letto"}
                                            checked={readCommunications.has(communication.id)}
                                            disabled={readCommunications.has(communication.id)}
                                            onChange={() => handleMarkAsRead(communication.id)}
                                        />
                                    </Pane>
                                )}
                            </Pane>
                            {userRole === 'direttore' && (
                                <Pane className="card-actions" display="flex" gap={8} marginTop={majorScale(2)}>
                                    <Button appearance="primary" intent="success" onClick={() => handleSelectCommunication(index)}>
                                        Modifica <EditIcon marginLeft={8} />
                                    </Button>
                                    <Button intent="danger" onClick={() => handleRemoveCommunication(index)}>
                                        Rimuovi <RemoveIcon marginLeft={8} />
                                    </Button>
                                </Pane>
                            )}
                        </>
                    )}
                    {typeof communication.attachments === 'string' && (
                        communication.attachments = JSON.parse(communication.attachments)
                    )}
                </Card>
            ))}
        </Pane>
    );
};

export default CommunicationList;